import { withErrorBoundary } from 'app/components/error-boundary';
import { NothingSelectedErrorModal } from 'app/components/nothing-selected-error-modal';
import { PopularItemSelectionList } from 'app/components/popular-item-selection-list';
import type { NavigationScreen } from 'app/types/navigation';
import { CategoryIcons, CategoryTab } from 'app/design/categories/tab';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { Page } from 'app/design/primitives/page';
import { useScopedTranslation } from 'app/hooks/i18n';
import { Trans } from 'react-i18next';
import React, { useEffect, useEffectEvent, useState } from 'react';
import { View } from 'react-native';
import { Row } from 'app/design/primitives/box';
import type { Category, PopularItem } from 'app/types/category';
import { usePopularItemsByCategory } from 'app/network/category';

import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useForm as useRHForm } from 'react-hook-form';
import { useQuickOffers } from 'app/hooks/use-quickoffers';
import { FlatListWithTabs } from 'app/atoms/flatlist-with-tabs';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { useAtomValue } from 'jotai';
import { newListingSourceAtom } from 'app/atoms';
import { Event, track } from 'app/lib/analytics';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import { getI18nLanguage } from 'app/lib/language';
import Responsive from 'app/components/responsive';
import { ScrollView } from 'react-native-gesture-handler';
import { isNotWhiteLabel } from 'app/utils/helpers';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();
const POPULAR_ITEMS_CONTENT_CONTAINER_STYLE = {
  paddingBottom: theme.spacing['spacing.general.x8'],
  flexGrow: 1,
  minHeight: 300,
};
const IS_HALLOWEEN_PROMOTION_ACTIVE = Date.now() < new Date('2024-11-01').getTime();

type Dictionary<T> = Record<string, T>;

//! OLD QUICK OFFERS - DEPRECATED ==> See Interests screen instead
const ChooseScreenComponent: NavigationScreen<'quick-new-choose'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const i18nLanguage = getI18nLanguage();
  const { control, handleSubmit } = useRHForm({
    mode: 'onChange',
  });
  const deviceWidth = useDeviceWidth();
  const { isLargeUI } = useUISize();
  const [carouselWidth, setCarouselWidth] = useState(0);

  const setPageTitle = usePageTitle('quick-new-choose');
  useEffect(() => setPageTitle('headerTitle'), [setPageTitle]);

  const newListingSource = useAtomValue(newListingSourceAtom);
  const trackOpened = useEffectEvent(() => {
    track(Event.QuickOffersOpened, { source: newListingSource });
  });
  useEffect(() => trackOpened(), []);

  // Load data from API
  const categoriesQuery = usePopularItemsByCategory();
  const [selectedItemIds, setSelectedItemIds] = useState<Set<PopularItem['id']>>(() => new Set());

  // Parse received data into tabs & categoriesByIcon
  const { tabs, categories } = (() => {
    const _tabs: Record<string, React.JSX.Element | null> = {};
    const _categories: Dictionary<Category> = {};
    const firstCategoryByColor = new Map<string, Category>();
    for (const cat of categoriesQuery.data ?? []) {
      _tabs[cat.name.en] = null;
      const category = {
        ...cat,
        popularItems: cat.popularItems?.map((item) => ({ ...item, selected: selectedItemIds.has(item.id) })),
      };
      if (!category.colorDark) {
        category.colorDark = theme.colors['text.primary'];
      }
      if (category.colorLight.toLowerCase() === '#ebbb4d') {
        // Multiple Halloween categories (same colorLight) = invert the colors of the 2nd category
        const firstCatWithThisColor = firstCategoryByColor.get(cat.colorLight) ?? cat;
        if (firstCatWithThisColor.icon !== cat.icon) {
          category.colorLight = firstCatWithThisColor.colorDark;
          category.colorDark = firstCatWithThisColor.colorLight;
        }
      }
      if (!firstCategoryByColor.has(cat.colorLight)) firstCategoryByColor.set(cat.colorLight, cat);
      _categories[cat.name.en] = category;
    }
    return { tabs: _tabs, categories: _categories };
  })();

  const formIsValid = selectedItemIds.size > 0;

  // Toggle (on/off) an item
  const toggleSelectedItem = (popItem: PopularItem, field: ControllerRenderProps<FieldValues, `row_${string}`>) => {
    const selected = !selectedItemIds.has(popItem.id);
    setSelectedItemIds((current) => {
      const next = new Set(current);
      if (selected) next.add(popItem.id);
      else next.delete(popItem.id);
      return next;
    });
    field.onChange(selected);
  };

  const [showErrorModal, setShowErrorModal] = useState(false);

  const onPress = async () => {
    if (!formIsValid) {
      setShowErrorModal(true);
      return;
    }
    await onSubmit();
  };

  // Continue to the next screen
  const { goToNext } = useQuickOffers();
  const onSubmit = handleSubmit(() => {
    const popularItems: PopularItem[] = [];
    for (const tab of Object.keys(tabs)) {
      const cat = categories[tab];
      for (const popItem of cat!.popularItems!) {
        if (popItem.selected) {
          popularItems.push({ ...popItem });
        }
      }
    }
    goToNext({ selectedItems: popularItems });
  });

  if (!categoriesQuery.data?.length) {
    // @TODO Loading view
    return <View />;
  }

  // How much of the next/previous pages do we see, on both sides
  const overflow = isLargeUI ? 0 : 0.08;

  const itemMargin = theme.spacing['spacing.general.x4'];
  const visibleTabCount = isLargeUI
    ? Math.max(1, Math.min(Object.keys(tabs).length, Math.max(2, Math.floor(carouselWidth / 260))))
    : 1;
  const scrollableWidth = isLargeUI ? carouselWidth : deviceWidth - 2 * itemMargin;
  const itemWidth = scrollableWidth / (visibleTabCount + overflow);

  const renderPopularItemsList = (item: string) => {
    return (
      <View
        style={{
          width: itemWidth - 2 * theme.spacing['spacing.general.x2'],
          margin: theme.spacing['spacing.general.x2'],
          borderRadius: theme.borderRadii['radius.xl'],
          backgroundColor: categories[item]!.colorLight,
          paddingHorizontal: isLargeUI ? 20 : 25,
          paddingVertical: 20,
        }}
      >
        <Text
          fontWeight="700"
          marginBottom="spacing.general.x2"
          style={{ color: categories[item]!.colorDark }}
          textAlign="center"
          variant="bodyLarge"
        >
          {categories[item]!.name[i18nLanguage]}
        </Text>

        <Responsive
          desktop={
            <ScrollView
              style={{ flex: 1, maxHeight: 300 }}
              contentContainerStyle={POPULAR_ITEMS_CONTENT_CONTAINER_STYLE}
            >
              <PopularItemSelectionList
                control={control}
                items={categories[item]!.popularItems}
                language={i18nLanguage}
                onToggle={toggleSelectedItem}
              />
            </ScrollView>
          }
          mobile={
            <PopularItemSelectionList
              control={control}
              items={categories[item]!.popularItems}
              language={i18nLanguage}
              onToggle={toggleSelectedItem}
            />
          }
        />
      </View>
    );
  };

  // Use icons (and colors) as tabs, instead of labels (the default in FlatListWithTabs)
  const tabsDefinition = Object.keys(tabs).map((tab) => ({
    image: <CategoryTab category={CategoryIcons[categories[tab]!.icon as keyof typeof CategoryIcons]} />,
    selectedImage: (
      <CategoryTab
        category={CategoryIcons[categories[tab]!.icon as keyof typeof CategoryIcons]}
        color={categories[tab]!.colorDark}
      />
    ),
    selectedColor: categories[tab]!.colorLight,
    value: tab,
  }));

  const tabsElements: Record<string, React.JSX.Element> = {};
  for (const cat of Object.keys(tabs)) {
    tabsElements[cat] = renderPopularItemsList(cat);
  }

  const categoryPager = (
    <FlatListWithTabs
      initialTab={Object.keys(tabs)[0] ?? ''}
      marginBelowTabs={theme.spacing['spacing.general.x5']}
      overflow={overflow}
      screenName="quick-new-choose"
      tabs={tabsElements}
      tabsDefinition={tabsDefinition}
      fixedContainerWidth={isLargeUI ? carouselWidth : undefined}
      visibleTabCount={visibleTabCount}
      showNavigationButtons={isLargeUI}
    />
  );

  const isHalloween = IS_HALLOWEEN_PROMOTION_ACTIVE && isNotWhiteLabel();

  return (
    <Page
      accessory={
        <Accessory>
          <Row justifyContent="flex-end" alignItems="center" gap="spacing.general.x2">
            {isLargeUI ? (
              <Button
                onPress={() => {
                  navigation.goBack();
                }}
                variant="whiteWithBorder"
              >
                <Text color="text.primary" fontWeight="500" textAlign="center">
                  {t('secondaryButtonText')}
                </Text>
              </Button>
            ) : null}

            <Button
              marginHorizontal="spacing.general.x4"
              marginTop={isLargeUI ? 'spacing.general.0' : 'spacing.general.x8'}
              onPress={() => void onPress()}
              variant="primary"
            >
              <Text color="surface.default" fontWeight={formIsValid ? '700' : '500'} textAlign="center">
                {t('buttonText')}
              </Text>
            </Button>
          </Row>
        </Accessory>
      }
    >
      <Text
        fontSize={18}
        lineHeight={22}
        marginBottom="spacing.general.x2"
        marginHorizontal="spacing.general.x8"
        textAlign="center"
      >
        <Trans
          components={{ strong: <Text fontWeight="700" /> }}
          i18nKey={`headerText${isHalloween ? 'Halloween' : ''}`}
          ns="quick-new-choose"
        />
      </Text>

      {isLargeUI ? (
        <View
          testID="quick-offers-desktop-carousel"
          style={{ flex: 1, width: '100%' }}
          onLayout={(event) => setCarouselWidth(event.nativeEvent.layout.width)}
        >
          {carouselWidth > 0 ? categoryPager : null}
        </View>
      ) : (
        categoryPager
      )}

      <NothingSelectedErrorModal
        buttonLabel={t('nothingSelectedError.buttonText')}
        message={t('nothingSelectedError.text')}
        onClose={() => setShowErrorModal(false)}
        open={showErrorModal}
        title={t('nothingSelectedError.title')}
      />
    </Page>
  );
};

export const ChooseScreen: NavigationScreen<'quick-new-choose'> = withErrorBoundary(ChooseScreenComponent);
